import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FormLabel, SimpleGrid } from "@chakra-ui/react";
import Loading from "../../../Component/Loading/loading";
import apiService from "../../../Method/Report/Report_Service";
import { ThemeProvider } from "@mui/material/styles";
import MUIDataTable from "mui-datatables";
import theme from "../../../assets/Table/TableTheme";
import createOptions from "../../../assets/Table/TableOption_Print2";
import { useDataContext } from "../../../DataProvider";
import Empty from "../../../Component/Empty/empty";

function generateDarkColor() {
  return {
    bg: `#999999`,
    text: "#fff",
    header: `#999999`
  };
}

const columns = (t, colorSet) => [
  {
    name: "USER_NAME",
    label: t("Employee"),
    options: {
      filter: false,
      sort: false,
      customHeadRender: (columnMeta) => (
        <th key={columnMeta.index} style={{ backgroundColor: colorSet.header, color: colorSet.text, padding: "9px", fontSize: "16px", fontWeight: "500", textAlign: "center" }}>{t("Employee")}</th>
      )
    }
  },
  {
    name: "BILL_NUMBER",
    label: t("Original_invoice"),
    options: {
      filter: false,
      sort: false,
      customHeadRender: (columnMeta) => (
        <th key={columnMeta.index} style={{ backgroundColor: colorSet.header, color: colorSet.text, padding: "9px", fontSize: "16px", fontWeight: "500", textAlign: "center" }}>{t("Original_invoice")}</th>
      )
    }
  },
  {
    name: "JOB_DATE",
    label: t("DATE"),
    options: {
      filter: false,
      sort: false,
      customHeadRender: (columnMeta) => (
        <th key={columnMeta.index} style={{ backgroundColor: colorSet.header, color: colorSet.text, padding: "9px", fontSize: "16px", fontWeight: "500", textAlign: "center" }}>{t("DATE")}</th>
      )
    }
  },
  {
    name: "D_TIME",
    label: t("Time"),
    options: {
      filter: false,
      sort: false,
      customHeadRender: (columnMeta) => (
        <th key={columnMeta.index} style={{ backgroundColor: colorSet.header, color: colorSet.text, padding: "9px", fontSize: "16px", fontWeight: "500", textAlign: "center" }}>{t("Time")}</th>
      ),
      customBodyRender: (value) => {
        if (!value) return "0%";
        const parts = value.split(",");
        return parts[1]?.trim() || value;
      }
    }
  },
  {
    name: "NOTE",
    label: t("NOTE"),
    options: {
      filter: false,
      sort: false,
      customHeadRender: (columnMeta) => (
        <th key={columnMeta.index} style={{ backgroundColor: colorSet.header, color: colorSet.text, padding: "9px", fontSize: "16px", fontWeight: "500", textAlign: "center", minWidth: "800px" }}>{t("NOTE")}</th>
      ),
      setCellProps: () => ({
        style: {
          whiteSpace: "normal",
          wordBreak: "break-word",
          textAlign: "right"
        }
      }),
      customBodyRender: (value) => (
        <div style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", whiteSpace: "normal", wordBreak: "break-word" }}>{value}</div>
      )
    }
  },
  {
    name: "AMO",
    label: t("Invoice_Amo"),
    options: {
      filter: false,
      sort: false,
      customHeadRender: (columnMeta) => (
        <th key={columnMeta.index} style={{ backgroundColor: colorSet.header, color: colorSet.text, padding: "9px", fontSize: "16px", fontWeight: "500", textAlign: "center" }}>{t("Invoice_Amo")}</th>
      ),
      customBodyRender: (value) => (value ? value.toLocaleString() : "0")
    }
  },
  {
    name: "BILL_BRANCH",
    label: t("Order_No"),
    options: {
      filter: false,
      sort: false,
      customHeadRender: (columnMeta) => (
        <th key={columnMeta.index} style={{ backgroundColor: colorSet.header, color: colorSet.text, padding: "9px", fontSize: "16px", fontWeight: "500", textAlign: "center" }}>{t("Order_No")}</th>
      )
    }
  },
  {
    name: "BRANCH_NAME",
    label: t("Branch_Name"),
    options: {
      filter: false,
      sort: false,
      customHeadRender: (columnMeta) => (
        <th key={columnMeta.index} style={{ backgroundColor: colorSet.header, color: colorSet.text, padding: "9px", fontSize: "16px", fontWeight: "500", textAlign: "center" }}>{t("Branch_Name")}</th>
      )
    }
  },
];

function App() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { trigger } = useDataContext();

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { success, data, error } = await apiService.search("USER_DETAILS_DELETE_REPORTS");

    if (success && data) {
      setData(data);
      setError("");
    } else {
      setError(error?.response?.data?.message || "حدث خطأ أثناء تحميل البيانات");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [trigger, fetchData]);

  useEffect(() => {
    if (!isLoading) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [isLoading]);

  return (
    <ThemeProvider theme={theme}>
      {error && <p className="error-message">{error}</p>}
      {isLoading && <Loading />}

      <div style={{ padding: "0 20px", }} className="Total_Header_Without_Bg">
        {!isLoading && (
          <SimpleGrid className="Total_Header" columns={{ base: 1, md: 2 }} mb={4}>
            <div>
              <FormLabel className="Total_Header_Right">{t("TOTAL_OPERATIONS")}</FormLabel>
              <FormLabel className="Total_Header_Value">{(data?.TOTAL_COUNT || 0).toLocaleString()}</FormLabel>
            </div>
            <div>
              <FormLabel className="Total_Header_Left">{t("TOTAL_AMO_ALL")}</FormLabel>
              <FormLabel className="Total_Header_Value">{(data?.ALL_TOTAL_AMO || 0).toLocaleString()}</FormLabel>
            </div>
          </SimpleGrid>
        )}
        {!isLoading && (data?.USER_DETAILS_DELETE?.length ?? 0) > 0 ? (
          data.USER_DETAILS_DELETE.map((group, index) => {
            const colorSet = generateDarkColor();
            return (
              <div key={index} style={{ marginBottom: "30px" }} className={`table-style-${index}`}>
                <style>{`
                  :root {
                    --scroll-color: ${colorSet.bg};
                  }
                  .table-style-${index} .MuiToolbar-root svg,
                  .table-style-${index} .MuiIconButton-root {
                    color: ${colorSet.bg} !important;
                  }
                  .table-style-${index} .MuiPaper-root {
                    overflow-x: auto;
                    scrollbar-color: var(--scroll-color) transparent;
                    scrollbar-width: thin;
                  }
                  .table-style-${index} .MuiPaper-root::-webkit-scrollbar {
                    height: 6px;
                  }
                  .table-style-${index} .MuiPaper-root::-webkit-scrollbar-thumb {
                    background-color: var(--scroll-color) !important;
                    border-radius: 4px;
                  }
                  .table-style-${index} .MuiPaper-root::-webkit-scrollbar-track {
                    background-color: #f1f1f1;
                  }
                  .css-1i7vg6w-MuiInputBase-root-MuiInput-root::before,
                  .css-1i7vg6w-MuiInputBase-root-MuiInput-root::after,
                  .css-1i7vg6w-MuiInputBase-root-MuiInput-root {
                    border-bottom: 1.5px solid ${colorSet.bg} !important;
                  }
                `}</style>

                <FormLabel style={{
                  marginBottom: "10px",
                  fontWeight: "bold",
                  backgroundColor: colorSet.bg,
                  color: colorSet.text,
                  padding: "10px 15px",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  textAlign: "right"
                }}>
                  {group.OPERATION}
                  {"  —  "}
                  {t("TOTAL_OPERATIONS")}: {(group.COUNT || 0).toLocaleString()}
                  {"  |  "}
                  {t("TOTAL_AMO_ALL")}: {(group.TOTAL_AMO || 0).toLocaleString()}
                </FormLabel>

                <MUIDataTable
                  title=""
                  data={group.DETAILS}
                  columns={columns(t, colorSet)}
                  options={{
                    ...createOptions(group.OPERATION, columns(t, colorSet), data.USER_DETAILS),
                    setTableProps: () => ({
                      style: {
                        backgroundColor: `${colorSet.bg}10`,
                        borderRadius: "10px",
                        overflowX: "auto"
                      }
                    }),
                    setRowProps: () => ({
                      style: {
                        backgroundColor: "#fff"
                      }
                    }),
                    setHeadCellProps: () => ({
                      style: {
                        backgroundColor: colorSet.header,
                        color: "#fff"
                      }
                    })
                  }}
                />
              </div>
            );
          })
        ) : (
          !isLoading && (data?.USER_DETAILS_DELETE?.length ?? 0) === 0 && (
            <div style={{ marginTop: "80px" }}>
              <Empty />
            </div>
          )
        )}
      </div>
    </ThemeProvider>
  );
}

export default App;